import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Sparkles, Leaf, ShieldCheck, MapPin } from "lucide-react";
import { SEO, localBusinessSchema } from "@/components/SEO";
import { Marquee } from "@/components/Marquee";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { BUSINESS } from "@/data/business";
import { SERVICES } from "@/data/services";

const HERO_IMG =
  "https://images.unsplash.com/photo-1706795033855-eee02f726868?crop=entropy&cs=srgb&fm=jpg&q=85";

const headlineWords = ["Restore", "Your", "Body.", "Renew", "Your", "Calm."];

const BENTO_CLASS = {
  "full-body-massage": "bento-a",
  "deep-tissue-massage": "bento-b",
  "thai-massage": "bento-c",
  "aromatherapy-massage": "bento-d",
};

const PHILOSOPHY = [
  {
    icon: Leaf,
    title: "Botanical Oils",
    text: "Cold-pressed, therapeutic-grade oils sourced for purity and fragrance.",
  },
  {
    icon: ShieldCheck,
    title: "Trained Therapists",
    text: "Skilled hands trained in traditional & therapeutic massage techniques.",
  },
  {
    icon: Sparkles,
    title: "Hygienic Sanctuary",
    text: "Fresh linens, sanitised rooms and calm, private treatment spaces.",
  },
  {
    icon: MapPin,
    title: "Easy to Reach",
    text: "Steps from Durgam Cheruvu Metro, above HDFC Bank in Madhapur.",
  },
];

const GALLERY = [
  "https://images.unsplash.com/photo-1737064295113-48299903f612?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1737064700128-c0769e13e211?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.pexels.com/photos/3654619/pexels-photo-3654619.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/15528975/pexels-photo-15528975.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

const TESTIMONIALS = [
  {
    quote:
      "The deep tissue session finally got rid of my chronic shoulder knots. The room was so calm I almost fell asleep.",
    name: "Priya R.",
  },
  {
    quote:
      "Loved the Thai massage — my back has never felt this flexible. Booked my next session before I even left.",
    name: "Karthik M.",
  },
  {
    quote:
      "Super easy to find near Durgam Cheruvu metro, and the aromatherapy blend they used was incredible.",
    name: "Ayesha S.",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.75]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <>
      <SEO
        title="Blossom Spa Madhapur | Full Body Massage, Thai & Aromatherapy Spa"
        description="Blossom Spa Madhapur offers full body massage, deep tissue, Thai & aromatherapy massage near Durgam Cheruvu Metro, Madhapur, Hyderabad. Call +91 7330619409 to book."
        path="/"
        schema={localBusinessSchema()}
      />

      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative h-[100vh] min-h-[640px] overflow-hidden flex items-end"
      >
        <motion.div
          style={{ y: imgY }}
          className="absolute inset-0 -top-[10%] h-[120%] w-full"
        >
          <img
            src={HERO_IMG}
            alt="Candlelit basalt stones and ambient spa decor at Blossom Spa Madhapur"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-gradient-to-t from-spa-slate via-spa-slate/40 to-spa-slate/10"
        />

        <motion.div
          style={{ opacity: contentOpacity }}
          className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28 w-full"
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-spa-blush mb-5">
            Madhapur, Hyderabad
          </p>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-medium text-spa-cream leading-[1.05] max-w-3xl">
            {headlineWords.map((word, i) => (
              <span key={i} className="inline-block overflow-hidden mr-3 sm:mr-4 align-bottom">
                <motion.span
                  className="inline-block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: 0.9,
                    delay: 0.15 + i * 0.1,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  {word}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-6 text-base sm:text-lg text-spa-cream/80 max-w-md"
          >
            Full body, deep tissue, Thai & aromatherapy massage therapies in
            a calm botanical sanctuary — moments from Durgam Cheruvu Metro.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <a
              href={`tel:${BUSINESS.phoneClean}`}
              data-testid="hero-call-button"
              className="flex items-center gap-2.5 bg-spa-terracotta text-spa-cream px-7 py-4 rounded-full text-sm font-medium hover:bg-spa-cream hover:text-spa-slate transition-all duration-300"
            >
              <Phone className="w-4 h-4" /> Call {BUSINESS.phoneDisplay}
            </a>
            <Link
              to="/services/full-body-massage"
              data-testid="hero-view-services-button"
              className="flex items-center gap-2.5 border border-spa-cream/40 text-spa-cream px-7 py-4 rounded-full text-sm font-medium hover:bg-spa-cream/10 transition-all duration-300"
            >
              View Services
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee
        items={[
          "DEEP TISSUE",
          "THAI MASSAGE",
          "FULL BODY MASSAGE",
          "AROMATHERAPY",
          "BLOSSOM SPA MADHAPUR",
        ]}
      />

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-spa-terracotta mb-4">
            Our Therapies
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-spa-slate max-w-xl">
            Four signature rituals, one calm sanctuary
          </h2>
        </Reveal>

        <div className="services-bento mt-12" data-testid="services-bento-grid">
          {SERVICES.map((service, i) => (
            <Reveal key={service.slug} delay={i * 0.08} className={BENTO_CLASS[service.slug]}>
              <Link
                to={service.path}
                data-testid={`service-card-${service.slug}`}
                className="group relative block h-full min-h-[220px] rounded-3xl overflow-hidden"
              >
                <img
                  src={service.cardImage}
                  alt={`${service.name} ambient decor at Blossom Spa Madhapur`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-spa-slate/90 via-spa-slate/20 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-7">
                  <h3 className="font-serif text-xl sm:text-2xl text-spa-cream">
                    {service.name}
                  </h3>
                  <p className="text-spa-cream/70 text-sm mt-2 max-w-xs hidden sm:block">
                    {service.excerpt}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-spa-blush text-sm font-medium group-hover:gap-3 transition-all duration-300">
                    Explore Therapy →
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-spa-muted py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-spa-terracotta mb-4">
              Why Blossom Spa
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-spa-slate max-w-xl">
              A sanctuary built on care & craft
            </h2>
          </Reveal>

          <RevealGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {PHILOSOPHY.map((item) => (
              <RevealItem key={item.title}>
                <div
                  data-testid={`philosophy-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-spa-surface rounded-3xl p-7 h-full border border-spa-slate/5 hover:shadow-[0_12px_32px_-8px_rgba(44,58,51,0.1)] transition-shadow duration-300"
                >
                  <item.icon className="w-7 h-7 text-spa-sage" strokeWidth={1.5} />
                  <h3 className="font-serif text-lg mt-5 text-spa-slate">
                    {item.title}
                  </h3>
                  <p className="text-sm text-spa-textMuted mt-2 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* GALLERY */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-spa-terracotta mb-4">
            The Atmosphere
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-spa-slate max-w-xl">
            Candlelight, calm & botanical scent
          </h2>
        </Reveal>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {GALLERY.map((src, i) => (
            <Reveal key={src} delay={i * 0.08} className="rounded-3xl overflow-hidden aspect-[3/4]">
              <img
                src={src}
                alt="Ambient spa decor at Blossom Spa Madhapur"
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
              />
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-spa-slate py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-spa-sage mb-4">
              Guest Reflections
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-spa-cream max-w-xl">
              Kind words from our guests
            </h2>
          </Reveal>

          <RevealGroup className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-14">
            {TESTIMONIALS.map((t) => (
              <RevealItem key={t.name}>
                <div
                  data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
                  className="bg-spa-cream/5 border border-spa-cream/10 rounded-3xl p-7 h-full"
                >
                  <p className="font-serif text-lg text-spa-cream/90 leading-relaxed italic">
                    “{t.quote}”
                  </p>
                  <p className="text-sm text-spa-sage mt-5">— {t.name}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-24 sm:py-32 text-center">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-spa-slate">
            Ready to unwind?
          </h2>
          <p className="text-spa-textMuted mt-4 max-w-md mx-auto">
            Call us directly to book your session — no forms, no waiting.
          </p>
          <a
            href={`tel:${BUSINESS.phoneClean}`}
            data-testid="cta-section-call-button"
            className="inline-flex items-center gap-2.5 bg-spa-slate text-spa-cream px-8 py-4 rounded-full text-sm font-medium mt-8 hover:bg-spa-slateHover transition-all duration-300"
          >
            <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
          </a>
        </Reveal>
      </section>
    </>
  );
};

export default Home;
